import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { MenuItem } from 'primeng/api';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { AuthService } from '../../../../../services/auth.service';
import { ToastService } from '../../../../../services/toast.service';
import { AutomaticPostStore } from '../../../../../stores/automatic-post.store';
import { UserStore } from '../../../../../stores/user.store';
import { AutomaticPostDetail, type AutomaticPostElementUi } from '../../../../../types/automatic-post.interface';
import { assetIdSignalFromRoute } from '../../../../../utils/asset-route.utils';
import { displayCardHeight, groupElementsIntoBands, resolveElementLayout } from '../../../../../utils/automatic-post-layout.utils';
import { AutomaticPostElementRendererComponent } from '../../../../ui/automatic-post-element-renderer/automatic-post-element-renderer.component';
import { CesAssetBreadcrumbComponent } from '../../../../ui/ces-asset-breadcrumb/ces-asset-breadcrumb.component';
import { CesButtonSecondaryComponent } from '../../../../ui/ces-buttons/ces-button-secondary.component';
import { CesTitleComponent } from '../../../../ui/ces-route-title/ces-route-title.component';
import { groupElementsBySection, type StructureBulkRefresh } from '../../analysis/publications/edit-session/publication-bulk-payload';

@Component({
  selector: 'app-home',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AutomaticPostElementRendererComponent,
    CesAssetBreadcrumbComponent,
    CesButtonSecondaryComponent,
    CesTitleComponent,
    FormsModule,
    SelectModule,
    SkeletonModule,
    TranslocoModule,
  ],
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly translate = inject(TranslocoService);
  private readonly translations = toSignal(this.translate.selectTranslation());
  private readonly authService = inject(AuthService);
  private readonly userStore = inject(UserStore);
  private readonly automaticPostStore = inject(AutomaticPostStore);
  private readonly toastService = inject(ToastService);

  readonly currentAssetId = assetIdSignalFromRoute(this.route);
  readonly selectedPostId = signal<number | null>(null);
  readonly structureRefresh = signal<StructureBulkRefresh | null>(null);
  readonly isLoadingStructure = signal<boolean>(false);

  // Last-write-wins guard: a slow request for an earlier selection must not
  // overwrite the structure once the user picked a more recent publication.
  private inflightRequestId = 0;

  readonly breadcrumbTrail = computed<MenuItem[]>(() => {
    this.translations();
    return [{ label: this.translate.translate('navigation.routes.dashboard') }];
  });

  readonly publicationOptions = computed<{ id: number; label: string }[]>(() => {
    const assetId = this.currentAssetId();
    if (!assetId) return [];
    const publications: AutomaticPostDetail[] = this.automaticPostStore.publicationsByAssetId(assetId);
    return publications.map(post => ({ id: post.id, label: post.name })).sort((a, b) => a.label.localeCompare(b.label));
  });

  // Same band model as the publication display view (and the emailed rendering):
  // full-width elements break out, the rest stacks per column so text cards hug
  // their content and pull the cards below them up.
  readonly sectionsWithElements = computed(() => {
    const refresh = this.structureRefresh();
    if (!refresh) return [];
    return groupElementsBySection(refresh).map(entry => ({
      ...entry,
      bands: groupElementsIntoBands(
        entry.elements.map(element => {
          const layout = resolveElementLayout(element.metadata, entry.section.columns);
          return {
            element,
            key: `el-${element.id}`,
            columnIndex: element.columnIndex ?? 0,
            sortNr: element.sortNr ?? 0,
            colSpan: layout.colSpan,
            rowSpan: layout.rowSpan,
          };
        }),
        entry.section.columns,
      ),
    }));
  });

  readonly canRefresh = computed(() => this.selectedPostId() !== null && !this.isLoadingStructure());

  protected cardHeight(element: AutomaticPostElementUi, section: { columns: number }): string | null {
    return displayCardHeight(element, section.columns);
  }

  ngOnInit(): void {
    void this.loadInitial();
  }

  async loadInitial(): Promise<void> {
    const ctx = this.requireContext();
    if (!ctx) return;

    try {
      await this.automaticPostStore.loadByAssetId(ctx.assetId);
    } catch (error) {
      this.reportPublicationFailure(error);
      return;
    }

    // Empty patch refreshes last_access on the row and returns the current pin,
    // mirroring how asset-sidebar tracks dashboard visits.
    try {
      const setting = await this.userStore.updateUserAssetSetting(ctx.userId, ctx.assetId);
      const pinned = setting.dashboardPostId;
      if (pinned !== null) {
        this.selectedPostId.set(pinned);
        await this.refreshStructure(pinned);
      }
    } catch {
      this.toastService.error('common.toasts.operationFailed');
    }
  }

  async onSelect(postId: number | null): Promise<void> {
    const ctx = this.requireContext();
    if (!ctx) return;

    const normalized = postId ?? null;
    this.selectedPostId.set(normalized);

    try {
      await this.userStore.updateUserAssetSetting(ctx.userId, ctx.assetId, { dashboardPostId: normalized });
    } catch {
      this.toastService.error('common.toasts.operationFailed');
      return;
    }

    if (normalized === null) {
      this.structureRefresh.set(null);
    } else {
      await this.refreshStructure(normalized);
    }
  }

  async refresh(): Promise<void> {
    const postId = this.selectedPostId();
    if (postId === null || this.isLoadingStructure()) return;
    await this.refreshStructure(postId);
  }

  private async refreshStructure(postId: number): Promise<void> {
    const requestId = ++this.inflightRequestId;
    this.isLoadingStructure.set(true);
    try {
      const refresh = await this.automaticPostStore.loadStructure(postId);
      if (requestId !== this.inflightRequestId) return;
      this.structureRefresh.set(refresh);
    } catch (error) {
      if (requestId !== this.inflightRequestId) return;
      this.structureRefresh.set(null);
      this.reportPublicationFailure(error);
    } finally {
      if (requestId === this.inflightRequestId) {
        this.isLoadingStructure.set(false);
      }
    }
  }

  // Le tableau de bord est le seul écran qui lit les publications sans que
  // l'utilisateur ait navigué dans leur menu : un refus de droit y arrive sans
  // contexte, et le message générique ne dit pas quelle permission manque.
  private reportPublicationFailure(error: unknown): void {
    const forbidden = error instanceof HttpErrorResponse && error.status === 403;
    this.toastService.error(forbidden ? 'common.toasts.publicationViewRightRequired' : 'common.toasts.operationFailed');
  }

  private requireContext(): { assetId: number; userId: number } | null {
    const assetId = this.currentAssetId();
    const userId = this.authService.currentUserId();
    return assetId && userId ? { assetId, userId } : null;
  }
}
